
import { defineComponent } from 'vue';
import Pagination from '@/components/Pagination/index.vue';
import { mapGetters } from 'vuex';
import LoadController from '@/js/LoadController';
import gql from 'graphql-tag';
import Logging from '@/logging';
import type { PropType } from 'vue';
import { format } from 'date-fns';
import AttachmentHelper from '@/helpers/attachment';
import { attachmentDownload } from '@/api/attachment';
import PlanManualActionDialog from '@/components/_dialogs/PlanManualActionDialog.vue';
import DisputeDialog from '@/components/_dialogs/DisputeDialog.vue';
import InvoiceDisputesDialog from '@/components/_dialogs/InvoiceDisputesDialog.vue';
import InterchangeLinks from '@/components/_Invoice/InterchangeLinks.vue';
import { Risolto_Channel_Enum, Risolto_Communication_Template, Risolto_Invoice_Document, Risolto_Tone_Of_Voice_Enum } from '@/generated/graphql';
import axios from 'axios';
import Utils from '@/utils/utils';
import CustomFieldValue from '@/components/_CustomFields/CustomFieldValue.vue';
import { Logger } from 'ag-grid-community';

interface Document {
	document_number: string;
	document_type: string;
	open_amount: number;
	total_amount: number;
	payment_reference: string;
	due_date: string;
	issue_date: string;
	recipient: string;
	legal_entity: string;
	participant: string;
	amount_currency: string;
	custom_fields: Record<string, any>;
	reference?: string;
	derived_paid_at?: string;
	invoice_dispute?: any[];
	status?: string;
	interchange_links?: any[];
	dunning_status?: any;
	file_references?: string[];
}

const GET_USER_INTERFACE_DETAILS = gql`
query ComponentsDocumentTable_userInterfaceDetails($participant: uuid!) {
	userInterfaceDetails: risolto_participant(
		where: {
			reference: { _eq: $participant }
		}
	) {
		custom_actions: user_interface_details(path: "invoice.custom_actions")
		custom_recipient_actions: user_interface_details(path: "recipient.custom_actions")
		custom_field_definitions: user_interface_details(path: "invoice.custom_field_definitions")
	}
}`;

const GET_ACTION = gql`
	query getAction($action: uuid!, $participant: uuid!) {
		action: risolto_action(where: { reference: { _eq: $action }, participant: { _eq: $participant } }) {
			status
			channel
			recipient
			recipient_by_reference {
				address
				balance_amount
				dunning_metadata
				last_dunning_at
				legal_id
				legal_id_type
				name
				account_id
				type
				updated_by
				participant
				reference
				legal_entity
				legal_entity_by_reference {
					reference
					registration_name
				}
			}
			reference
			metadata
			message_code
			participant
			primary_invoice
			primary_invoice_by_reference {
				invoice_number
			}
			action_invoices {
				reference
				relation
				invoice_by_reference {
					invoice_number
					open_amount
					total_amount
					payment_reference
					due_date
					issue_date
					status
					recipient
					recipient_by_reference {
						reference
						name
					}
					legal_entity
					legal_entity_by_reference {
						reference
						registration_name
					}
					participant
					participant_by_reference {
						name
						reference
						logical_id
					}
					reference
					amount_currency
					interchange_links {
						interchange_by_reference {
							reference
							channel
							direction
						}
					}
				}
			}
			legal_entity
			execution_type
			source
			executed_at
			original_action
			retry_count
			start_time
			due_date
			recipient
			recipient_by_reference {
				reference
				name
				account_id
				participant
			}
			legal_entity_by_reference {
				registration_name
				outbound_channel: processing_details(path: "outbound_channel")
			}
			created_at
			assignee
			assignee_by_reference {
				username
			}
			channel_endpoint
			channel_endpoint_by_reference {
				channel
				endpoint
				endpoint_details
				deactivated_at
				deactivated_by
				last_failed_delivery_at
				last_failed_delivery_type
				locale
				priority
			}
		}
	}
`;

export default defineComponent({
	name: 'DocumentTable',

	apollo: {
		userInterfaceDetails: {
			query: GET_USER_INTERFACE_DETAILS,
			variables() {
				return {
					participant: this.selectedParticipantStore
				};
			},
			update: data => data.userInterfaceDetails[0],
			skip() {
				return !this.formattedData;
			}
		}
	},

	components: {
		Pagination,
		PlanManualActionDialog,
		DisputeDialog,
		InvoiceDisputesDialog,
		InterchangeLinks,
		CustomFieldValue
	},

	props: {
		view: {
			type: String as PropType<string | undefined>,
			required: false,
			default: undefined
		},
		fullRecipient: {
			type: Object as PropType<Record<string, any>>,
			required: false,
			default: () => ({})
		},
		recipient: {
			type: String,
			required: true
		},
		disablePaymentPlan: {
			type: Boolean,
			required: false,
			default: false
		},
		disablePaymentPlanReason: {
			type: String,
			required: false,
			default: () => null
		}
	},

	data() {
		return {
			multipleSelection: [] as Document[],
			page: 1,
			pageSize: 25,
			searchQuery: '',
			activeSearchQuery: '',
			loadController: new LoadController(),
			showAllInvoices: false,
			userInterfaceDetails: null as any,
			planManualActionDialogVisible: false,
			selectedInvoice: null,
			disputeDialogVisible: false,
			invoiceDisputesDialogVisible: false,
			sendAccountOverviewActionEmailLoading: false,
			sendAccountOverviewActionSmsLoading: false,
			sortField: 'due_date',
			sortOrder: 'ascending'
		};
	},

	computed: {
		...mapGetters(['selectedParticipantStore']),

		loading(): boolean {
			return !this.loadController.failed && this.loadController.data === null;
		},

		selectedInvoiceReferences() {
			return this.multipleSelection.filter(doc => doc.document_type === 'INVOICE').map(doc => doc.reference);
		},

		selectedInvoices() {
			return this.multipleSelection.filter(doc => doc.document_type === 'INVOICE');
		},

		formattedData(): Document[] {
			const data = this.loadController.data === null ? [] : this.loadController.data;

			return data.map(doc => {
				const flattenedDoc = { ...doc };

				// Add custom fields as direct properties
				if (this.userInterfaceDetails?.custom_field_definitions) {
					this.userInterfaceDetails.custom_field_definitions.forEach(customFieldDef => {
						const value = this.getValueByPath(doc.custom_fields || {}, customFieldDef.path);
						flattenedDoc[customFieldDef.path] = value;
					});
				}

				// Compute days due
				if (doc.due_date && doc.open_amount !== 0) {
					try {
						const dueDate = new Date(doc.due_date);
						const now = new Date();
						const diffTime = now.getTime() - dueDate.getTime();
						flattenedDoc.daysDue = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
					} catch (error) {
						flattenedDoc.daysDue = null;
					}
				} else {
					flattenedDoc.daysDue = null;
				}

				return flattenedDoc;
			});
		},

		filteredData(): Document[] {
			let data = [...this.formattedData];

			if (this.activeSearchQuery) {
				data = data.filter(doc =>
					doc.document_number.toLowerCase().includes(this.activeSearchQuery.toLowerCase())
				);
			}

			if (!this.showAllInvoices) {
				data = data.filter(doc => doc.open_amount !== 0);
			}

			return data;
		},

		sortedAndFilteredData(): Document[] {
			const data = [...this.filteredData];

			if (!this.sortField) {
				return data;
			}

			return this.sortData(data, this.sortField, this.sortOrder);
		},

		paginatedData(): Document[] {
			const start = (this.page - 1) * this.pageSize;
			const end = start + this.pageSize;
			return this.sortedAndFilteredData.slice(start, end);
		},

		selectedSum(): number {
			return this.multipleSelection.reduce((sum, doc) => sum + (doc.open_amount || 0), 0);
		}
	},
	watch: {
		selectedParticipantStore() {
			this.setupQueryVariables();
		},
		recipient() {
			this.setupQueryVariables();
		}
	},
	created() {
		this.setupQueryVariables();
	},
	methods: {
		computedStatus(openAmount: number, dueDate: string | null): string {
			return Utils.computedStatus(openAmount, dueDate);
		},

		// Sort-related methods
		isDateString(value: any): boolean {
			if (!value || typeof value !== 'string') return false;
			// Match ISO date formats like "2023-12-25" or "2023-12-25T10:30:00"
			return /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?/.test(value) && !isNaN(Date.parse(value));
		},

		sortData(data: Document[], sortField: string, sortOrder: string): Document[] {
			return data.sort((a, b) => {
				let aValue = this.getValueByPath(a, sortField);
				let bValue = this.getValueByPath(b, sortField);

				// Auto-detect and convert date strings to Date objects for proper sorting
				if (this.isDateString(aValue) && this.isDateString(bValue)) {
					aValue = new Date(aValue);
					bValue = new Date(bValue);
				} else if (this.isDateString(aValue)) {
					aValue = new Date(aValue);
				} else if (this.isDateString(bValue)) {
					bValue = new Date(bValue);
				}

				// Handle null/undefined values
				if (aValue == null && bValue == null) return 0;
				if (aValue == null) return sortOrder === 'ascending' ? 1 : -1;
				if (bValue == null) return sortOrder === 'ascending' ? -1 : 1;

				// Handle empty string values
				const aIsEmpty = aValue === '';
				const bIsEmpty = bValue === '';
				if (aIsEmpty && bIsEmpty) return 0;
				if (aIsEmpty) return sortOrder === 'ascending' ? 1 : -1;
				if (bIsEmpty) return sortOrder === 'ascending' ? -1 : 1;

				let result = 0;

				// Handle different data types
				if (typeof aValue === 'number' && typeof bValue === 'number') {
					result = aValue - bValue;
				} else if (aValue instanceof Date && bValue instanceof Date) {
					result = aValue.getTime() - bValue.getTime();
				} else {
					result = String(aValue).localeCompare(String(bValue));
				}

				return sortOrder === 'descending' ? -result : result;
			});
		},

		handleSortChange({ column, prop, order }: { column: any; prop: string; order: string | null }) {
			if (order === null) {
				// Clear sorting - but keep default sort
				this.sortField = 'due_date';
				this.sortOrder = 'ascending';
			} else {
				this.sortField = prop;
				this.sortOrder = order;
			}
			// Reset to first page when sorting changes
			this.page = 1;
		},

		sortCustomFieldMethod(a: Document, b: Document, fieldPath: string): number {
			const aValue = this.getValueByPath(a.custom_fields || {}, fieldPath);
			const bValue = this.getValueByPath(b.custom_fields || {}, fieldPath);

			// Handle null/undefined values
			if (aValue == null && bValue == null) return 0;
			if (aValue == null) return 1; // Put nulls at the end
			if (bValue == null) return -1;

			// Handle empty string values
			const aIsEmpty = aValue === '';
			const bIsEmpty = bValue === '';
			if (aIsEmpty && bIsEmpty) return 0;
			if (aIsEmpty) return 1; // Put empty strings at the end
			if (bIsEmpty) return -1;

			// Auto-detect and handle date strings
			if (this.isDateString(aValue) && this.isDateString(bValue)) {
				const aDate = new Date(aValue);
				const bDate = new Date(bValue);
				return aDate.getTime() - bDate.getTime();
			}

			// Handle numbers
			if (typeof aValue === 'number' && typeof bValue === 'number') {
				return aValue - bValue;
			}

			// Default to string comparison
			return String(aValue).localeCompare(String(bValue));
		},

		handleCreatePhoneAction() {
			this.$store.commit('actions/SET_CREATE_PHONE_ACTION_OVERLAY_VISIBLE', true);
			this.$store.commit('actions/SET_CREATE_PHONE_ACTION_OVERLAY_INVOICES', this.selectedInvoices);
			this.$store.commit('actions/SET_CREATE_PHONE_ACTION_OVERLAY_RECIPIENT', this.fullRecipient);
		},

		handlePlanManualAction() {
			this.planManualActionDialogVisible = true;
		},

		handleDisputeInvoices() {
			this.disputeDialogVisible = true;
		},
		handleInvoiceDisputeConfirm() {
			this.fetchDocuments();
		},
		handleInvoiceDisputeChange() {
			this.fetchDocuments();
		},
		handleInvoiceDisputeClick(invoice) {
			this.selectedInvoice = invoice;
			this.invoiceDisputesDialogVisible = true;
		},

		handleCreatePaymentPlanButtonClick() {
			this.$router.push({
				name: 'create-payment-plan',
				params: { participant: this.selectedParticipantStore },
				query: { recipient: this.recipient }
			});
		},

		setupQueryVariables() {
			this.page = 1;

			if (this.selectedParticipantStore) {
				this.fetchDocuments();
			}
		},

		onPaginationChange({ page, limit }: { page: number; limit: number }) {
			this.page = page;
			this.pageSize = limit;
		},

		onFilterChange() {
			this.page = 1;
		},

		async fetchDocuments() {
			Logging.log('DocumentTable.fetchDocuments');
			this.loadController.clear();
			this.loadController.didAsk = true;
			this.loadController.failed = false;

			try {
				const { data: documentsData } = await this.$apollo.query({
					query: this.makeQuery(),
					variables: {
						participantRef: this.selectedParticipantStore,
						legalEntity: this.fullRecipient.legal_entity,
						recipient: this.recipient
					}
				});

				this.loadController.data = documentsData.documents.map(doc => {
					return {
						reference: doc.v_reference,
						...doc.data
					};
				});
				this.loadController.didAsk = false;

				// Batch fetch additional document data
				await this.fetchAllDocumentDataBatch();
			} catch (error) {
				Logging.log('ERROR DocumentTable.fetchDocuments', error);
				this.loadController.data = null;
				this.loadController.failed = true;
			}
		},

		async fetchAllDocumentDataBatch() {
			if (!this.loadController.data || this.loadController.data.length === 0) {
				return;
			}

			try {
				const additionalData = await this.fetchBatchDocumentData();
				this.mergeAdditionalDataWithDocuments(additionalData);
			} catch (error) {
				Logging.log('Error fetching batch document data:', error);
			}
		},

		async fetchBatchDocumentData() {
			const variables = {
				participant: this.selectedParticipantStore,
				recipient: this.recipient
			};

			const response = await this.$apollo.query({
				query: this.createBatchQuery(),
				variables
			});

			return {
				invoices: response.data.invoices || [],
				creditNotes: response.data.creditNotes || [],
				debitNotes: response.data.debitNotes || [],
				payments: response.data.payments || []
			};
		},

		createBatchQuery() {
			return gql`
				query getBatchDocumentData($participant: uuid!, $recipient: uuid!) {
					invoices: risolto_invoice_document(
						where: { 
							participant: { _eq: $participant },
							recipient: { _eq: $recipient }
						}
					) {
						invoice_number
						reference
						actions(where: { executed_at: { _is_null: false } }) {
							action_interchanges {
								interchange_by_reference {
									interchange_events {
										event_type
										last_event_timestamp
									}
								}
							}
						}
					}

					creditNotes: risolto_credit_note(
						where: { 
							participant: { _eq: $participant },
							recipient: { _eq: $recipient }
						}
					) {
						credit_note_number
						reference
					}

					debitNotes: risolto_debit_note(
						where: { 
							participant: { _eq: $participant },
							recipient: { _eq: $recipient }
						}
					) {
						debit_note_number
						reference
					}

					payments: risolto_payment_document(
						where: { 
							participant: { _eq: $participant },
							recipient: { _eq: $recipient }
						}
					) {
						payment_number
						reference
					}
				}
			`;
		},

		getAdditionalDataForDocument(documentNumber: string, documentType: string, additionalData: any) {
			switch (documentType) {
				case 'INVOICE':
					return additionalData.invoices.find((inv: any) =>
						inv.invoice_number === documentNumber
					);
				case 'CREDIT_NOTE':
					return additionalData.creditNotes.find((cn: any) =>
						cn.credit_note_number === documentNumber
					);
				case 'DEBIT_NOTE':
					return additionalData.debitNotes.find((dn: any) =>
						dn.debit_note_number === documentNumber
					);
				case 'PAYMENT':
					return additionalData.payments.find((payment: any) =>
						payment.payment_number === documentNumber
					);
				default:
					return null;
			}
		},

		mergeAdditionalDataWithDocuments(additionalData: any) {
			this.loadController.data = this.loadController.data.map((doc: Document) => {
				const additionalDocData = this.getAdditionalDataForDocument(
					doc.document_number,
					doc.document_type,
					additionalData
				);
				return additionalDocData ? this.mergeDocumentData(doc, additionalDocData) : doc;
			});
		},

		mergeDocumentData(doc: Document, additionalData: any) {
			const merged = {
				...doc,
				reference: additionalData.reference,
				interchange_links: additionalData.actions?.flatMap((action: any) =>
					action.action_interchanges || []
				) || []
			};

			return merged;
		},

		handleSelectionChange(val: Document[]) {
			this.multipleSelection = val;
			this.$store.dispatch('risolto/setSelectedDocuments', val);

			if (this.multipleSelection.length === 1) {
				this.$store.commit('risolto/SET_NEW_NOTE_TYPE', 'single-invoice');
			} else if (this.multipleSelection.length >= 1) {
				this.$store.commit('risolto/SET_NEW_NOTE_TYPE', 'multi-invoice');
			} else if (this.multipleSelection.length === 0) {
				this.$store.commit('risolto/SET_NEW_NOTE_TYPE', 'recipient');
			} else {
				Logging.log("Can't determine new note type");
			}
		},

		handleCustomActionClick(row, action) {
			Logging.log('handleCustomActionClick', row, action);
			const payload = {
				participant: row.participant,
				legal_entity: row.legal_entity,
				recipient: row.recipient_by_reference.reference,
				document_type: row.document_type,
				document_number: row.document_number,
				user: this.$store.state.risolto.loggedInUser.reference
			};

			this.$prompt(this.$t('custom_action.confirm', { invoice_number: row.document_number }), action.label[this.$i18n.locale], {
				confirmButtonText: this.$t('Ok'),
				cancelButtonText: this.$t('cancel'),
				inputPattern: new RegExp(`^${row.document_number}$`),
				inputErrorMessage: this.$t('validator.invalid_number')
			}).then(async ({ value }) => {
				this.$message({
					type: 'info',
					message: this.$t('custom_action.started')
				});
				const response = await axios.post(action.endpoint, payload, {
					headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
				});
				if (response.data && response.data.redirect) {
					Utils.openInNewTab(response.data.redirect);
				}
				this.$forceUpdate();
				this.$message({
					type: 'info',
					message: this.$t('custom_action.executed')
				});
			}).catch(() => {
				this.$message({
					type: 'info',
					message: this.$t('custom_action.not_executed')
				});
			});
		},
		async handleCustomRecipientActionClick(recipient, action) {
			Logging.log('handleCustomRecipientActionClick', recipient, action);
			const payload = {
				participant: recipient.participant,
				legal_entity: recipient.legal_entity,
				recipient: recipient.reference
			};

			let response = await axios.post(action.endpoint, payload, {
				headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
			});

			if (response.data.redirect) {
				Utils.openInNewTab(response.data.redirect);
			}
		},

		async handleSendAccountOverviewEmail() {
			this.sendAccountOverviewActionEmailLoading = true;
			try {
				await this.handleSendAccountOverview(Risolto_Channel_Enum.Email);
			} finally {
				this.sendAccountOverviewActionEmailLoading = false;
			}
		},
		async handleSendAccountOverviewSms() {
			this.sendAccountOverviewActionSmsLoading = true;
			try {
				await this.handleSendAccountOverview(Risolto_Channel_Enum.Sms);
			} finally {
				this.sendAccountOverviewActionSmsLoading = false;
			}
		},
		async handleSendAccountOverview(channel: Risolto_Channel_Enum) {
			this.sendAccountOverviewActionLoading = true;

			const enabledDamageClauses = this.fullRecipient.legal_entity_by_reference?.account_overview?.enabled_damage_clauses ?? true;
			const enabledReminderCosts = this.fullRecipient.legal_entity_by_reference?.account_overview?.enabled_reminder_costs ?? true;

			const payload = {
				participant: this.$store.state.risolto.selectedParticipantStore,
				legal_entity: this.fullRecipient.legal_entity,
				recipient: this.fullRecipient.reference,
				channel: channel,
				enabled_damage_clauses: enabledDamageClauses,
				enabled_reminder_costs: enabledReminderCosts
			};

			const response = await axios.post(`https://${process.env.VUE_APP_API_ENDPOINT}/action/create-account-overview`, payload, {
				headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
			});
			const result = await this.$apollo.query({
				query: GET_ACTION,
				variables: {
					participant: this.$store.state.risolto.selectedParticipantStore,
					action: response.data.action.reference
				}
			});
			const action = result.data.action[0];

			this.$store.commit('actions/UPSERT_ACTION', action);
			this.$store.commit('actions/SET_SELECTED_ACTION_REF', action.reference);
			this.$store.commit('risolto/SET_SELECTED_RECIPIENT', action.recipient_by_reference);
			this.$store.commit('risolto/SET_COMPONENT_TYPE_STORE', action.channel);
			this.$store.commit('risolto/SET_ACTION_DETAIL_DIALOG_VISIBLE', true);
		},
		async handleManualAttachmentButtonClick() {
			this.manualAttachmentLoading = true;
			const template: Risolto_Communication_Template = await this.$refs.attachmentTemplatesDialog.show();

			if (template == null) {
				this.manualAttachmentLoading = false;
				return;
			}

			const payload = {
				channel: template.channel,
				tone_of_voice: Risolto_Tone_Of_Voice_Enum.Friendly,
				locale: template.locale,
				template_id: template.template_id,
				participant: this.$store.state.risolto.selectedParticipantStore,
				template_query_variables: {
					participant: this.$store.state.risolto.selectedParticipantStore,
					recipient: this.fullRecipient.reference,
					invoices: this.selectedInvoices.map((invoice: Risolto_Invoice_Document) => invoice.reference)
				}
			};

			axios
				.post(`https://${process.env.VUE_APP_API_ENDPOINT}/templating/frontend-render/v1`, payload, {
					headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
				})
				.then(result => {
					if (result.data.message_code === 'generic_failure') {
						this.$notify({
							title: this.$t('somethingWentWrong'),
							type: 'error'
						});
						this.manualAttachmentLoading = false;
						return;
					}
					AttachmentHelper.showDocument(result.data.base64_source || result.data.base64, result.data.content_type || 'text/html');
				})
				.catch(error => {
					Logging.error(error);
					this.$notify({
						title: this.$t('somethingWentWrong'),
						type: 'error'
					});
				})
				.finally(() => {
					this.manualAttachmentLoading = false;
				});
		},

		async handleActionItemClick(item) {
			Logging.log('handleActionItemClick', item);
			if (!item || !item.reference) {
				return;
			}
			const result = await this.$apollo.query({
				query: GET_ACTION,
				variables: {
					participant: this.$store.state.risolto.selectedParticipantStore,
					action: item.reference
				}
			});
			const action = result.data.action[0];
			if (action.channel === 'PHONE') {
				this.$store.commit('risolto/SET_ACTION_DETAIL_DIALOG_VISIBLE', false);
				this.$store.dispatch('risolto/setActionPhoneDetailOverlayAction', action.reference);
				this.$store.dispatch('risolto/setActionPhoneDetailOverlayVisible', true);
			} else {
				this.$store.dispatch('risolto/setActionPhoneDetailOverlayVisible', false);
				this.$store.commit('actions/UPSERT_ACTION', action);
				this.$store.commit('actions/SET_SELECTED_ACTION_REF', action.reference);
				this.$store.commit('risolto/SET_SELECTED_RECIPIENT', action.recipient_by_reference);
				this.$store.commit('risolto/SET_COMPONENT_TYPE_STORE', action.channel);
				this.$store.commit('risolto/SET_ACTION_DETAIL_DIALOG_VISIBLE', true);
			}
		},

		makeQuery() {
			return gql`
				query getDocumentsForTable(
					$participantRef: uuid!
					$legalEntity: uuid!
					$recipient: uuid!
				) {
					documents: risolto_fe_recipient_accounting_document(
						args: { 
							p_participant: $participantRef, 
							p_legal_entity: $legalEntity,
							p_recipient: $recipient
						}
					) {
						v_reference
						data
					}
				}
			`;
		},

		async onClickDocIcon(val) {
			attachmentDownload(this.selectedParticipantStore, val);
		},

		isPdf(attachment) {
			return AttachmentHelper.isPdf(attachment);
		},
		isXml(attachment) {
			return AttachmentHelper.isXml(attachment);
		},

		formatDate(date: string): string {
			try {
				return format(new Date(date), 'dd-MM-yyyy');
			} catch (error) {
				Logging.log('Error formatting date:', error);
				return '';
			}
		},

		formatNumber(value: number): string {
			try {
				const formatted = new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(Math.abs(value));

				return value < 0 ? `- ${formatted}` : formatted;
			} catch (error) {
				Logging.log('Error formatting number:', error);
				return '';
			}
		},

		onSearchChange() {
			this.activeSearchQuery = this.searchQuery;
			this.page = 1;
		},

		handleReset() {
			this.searchQuery = '';
			this.activeSearchQuery = '';
			this.showAllInvoices = false;
			this.page = 1;
		},

		formatDaysDue(due_date: string): number {
			try {
				const dueDate = new Date(due_date);
				const now = new Date();
				const diffTime = now.getTime() - dueDate.getTime();
				const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

				return diffDays;
			} catch (error) {
				Logging.log('Error formatting days due:', error);
				return 0;
			}
		},

		getValueByPath(obj, path) {
			// https://stackoverflow.com/questions/6491463/accessing-nested-javascript-objects-and-arrays-by-string-path
			const resolvePath = (object, path, defaultValue) => path
				.split(/[\.\[\]\'\"]/)
				.filter(p => p)
				.reduce((o, p) => o ? o[p] : defaultValue, object);

			return resolvePath(obj, path, '');
		}

	}
});
